import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from '@weezevent/nacre';
import { fetcher } from '@weezevent/weezjs-core';
import { useTranslation } from 'react-i18next';

import { useInterval } from '../../utils/hooks';
import { BACKOFF_FACTOR, DONE, FIRST_INTERVAL, MAX_INTERVAL, MAX_POLL_FAILURES, MAX_TRACKING_DURATION, NEEDS_ATTENTION_STATUSES, STORAGE_KEY } from './constants';

const readStoredExports = () => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        const parsed = stored ? JSON.parse(stored) : [];
        if (!Array.isArray(parsed)) {
            return [];
        }
        // Drop exports we have been tracking for too long to be still relevant
        return parsed.filter(({ startedAt }) => !startedAt || Date.now() - startedAt < MAX_TRACKING_DURATION);
    } catch (e) {
        // Corrupted payload, start over rather than blocking every future export
        return [];
    }
};

// Writing can fail when the storage is full or unavailable (private browsing, browser settings).
// Returns whether the exports could be persisted, so the caller can fall back rather than losing them.
const writeStoredExports = exports => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(exports));
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Tracks asynchronous exports at app level, so the user gets notified when their export is ready
 * even if they navigated away from the page they launched it from.
 *
 * Each tracked export is persisted in the local storage, which also lets it survive a page reload.
 */
export const useExportNotifications = () => {
    const { t } = useTranslation();
    // visibilitychange only fires on later changes, so the initial state has to check the current
    // visibility too: reloading an already hidden tab should not start polling either.
    const [delay, setDelay] = React.useState(() => (!document.hidden && readStoredExports().length ? FIRST_INTERVAL : null));
    const isProcessingRef = React.useRef(false);

    const notify = React.useCallback(
        (organizationId, messageTrans, linkTrans, appearance) => {
            toast[appearance](
                <>
                    {t(messageTrans)} <Link to={`/ticket/O${organizationId}/exports`}>{t(linkTrans)}</Link>
                </>
            );
        },
        [t]
    );

    const handleFinalState = React.useCallback(
        ({ organizationId }, response) => {
            if (response.status === DONE) {
                notify(organizationId, 'ticket.export.async_complete', 'ticket.export.async_complete_link', 'success');
                //  a tab opened from a background poll (so without any user interaction)
                // can be blocked by the browser, hence the download link in the toast above.
                if (response.dest) {
                    window.open(response.dest, '_blank');
                }
                return;
            }

            notify(organizationId, 'ticket.export.async_failed', 'ticket.export.async_failed_link', 'error');
        },
        [notify]
    );

    const checkPendingExports = React.useCallback(() => {
        if (isProcessingRef.current) {
            return;
        }

        const pendingExports = readStoredExports();
        if (!pendingExports.length) {
            setDelay(null);
            return;
        }

        isProcessingRef.current = true;

        Promise.all(
            pendingExports.map(pendingExport =>
                fetcher
                    .get(pendingExport.url)
                    .then(({ response, status }) => {
                        if (status !== 200) {
                            return pendingExport;
                        }
                        if (response?.status === DONE || NEEDS_ATTENTION_STATUSES.includes(response?.status)) {
                            handleFinalState(pendingExport, response);
                            return null;
                        }
                        //  reset the failure count
                        return { ...pendingExport, failures: 0 };
                    })
                    .catch(() => {
                        const failures = (pendingExport.failures || 0) + 1;
                        return failures <= MAX_POLL_FAILURES ? { ...pendingExport, failures } : null;
                    })
            )
        )
            .then(results => {
                const stillPending = results.filter(Boolean);
                // Re-read to keep exports added while the requests were in flight
                const addedMeanwhile = readStoredExports().filter(stored => !pendingExports.some(({ url }) => url === stored.url));
                const remaining = [...stillPending, ...addedMeanwhile];

                // Stop polling if the exports we just notified about cannot be removed from the
                // storage, rather than notifying about them again on every following poll.
                const persisted = writeStoredExports(remaining);

                setDelay(current => (persisted && remaining.length ? Math.min(Math.round((current || FIRST_INTERVAL) * BACKOFF_FACTOR), MAX_INTERVAL) : null));
            })
            .finally(() => {
                isProcessingRef.current = false;
            });
    }, [handleFinalState]);

    useInterval(checkPendingExports, delay);

    // No need to poll for a tab nobody is looking at, and the user can't act on the toast anyway.
    // Polling restarts at full speed when they come back.
    React.useEffect(() => {
        const onVisibilityChange = () => {
            setDelay(document.hidden || !readStoredExports().length ? null : FIRST_INTERVAL);
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    // Returns whether the export could be tracked, so the caller can fall back to its own polling.
    const addExportNotification = React.useCallback(({ url, organizationId }) => {
        if (!url || !writeStoredExports([...readStoredExports(), { url, organizationId, startedAt: Date.now() }])) {
            return false;
        }

        // Restart at full speed so a freshly queued export is picked up quickly, unless the user
        // already left the tab: the visibility listener restarts the polling when they come back.
        setDelay(document.hidden ? null : FIRST_INTERVAL);
        return true;
    }, []);

    return { addExportNotification };
};
